'use client';

import { CheckCircle2, Info, X, XCircle } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

/**
 * Minimál toast-rendszer felhasználói visszajelzéshez. Azokhoz a műveletekhez
 * kell, amelyeknek nincs látható DOM-következménye (pl. meghívó-újraküldés,
 * jelszó-reset link küldése) — enélkül a felhasználó nem tudja, történt-e bármi.
 *
 * Portállal a `body`-ra renderel (a kártyák `transform`-ja fölé), 4 mp után
 * automatikusan eltűnik, kézzel is zárható.
 */
type ToastVariant = 'success' | 'error' | 'info';
type ToastItem = { id: number; message: string; variant: ToastVariant };
type ToastFn = (t: { message: string; variant?: ToastVariant }) => void;

const ToastContext = createContext<ToastFn | null>(null);

const VARIANS: Record<ToastVariant, { Icon: typeof CheckCircle2; cls: string }> = {
  success: { Icon: CheckCircle2, cls: 'border-green-200 bg-green-50 text-green-800' },
  error: { Icon: XCircle, cls: 'border-red-200 bg-red-50 text-red-800' },
  info: { Icon: Info, cls: 'border-brand-200 bg-brand-50 text-brand-800' },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [mounted, setMounted] = useState(false);
  const nextId = useRef(1);

  useEffect(() => setMounted(true), []);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback<ToastFn>(({ message, variant = 'info' }) => {
    const id = nextId.current++;
    setToasts((prev) => [...prev, { id, message, variant }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
  }, []);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {mounted
        ? createPortal(
            <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2">
              {toasts.map(({ id, message, variant }) => {
                const { Icon, cls } = VARIANS[variant];
                return (
                  <div
                    key={id}
                    role="status"
                    className={`pointer-events-auto flex items-start gap-2 rounded-xl border px-4 py-3 text-sm shadow-lg ${cls}`}
                  >
                    <Icon className="mt-0.5 size-4 shrink-0" />
                    <span className="flex-1">{message}</span>
                    <button
                      type="button"
                      onClick={() => remove(id)}
                      aria-label="Bezárás"
                      className="shrink-0 opacity-60 transition-opacity hover:opacity-100"
                    >
                      <X className="size-4" />
                    </button>
                  </div>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </ToastContext.Provider>
  );
}

/** A toast-küldő függvény. Provider nélkül hibát dob (nincs néma fallback). */
export function useToast(): ToastFn {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast csak ToastProvider-en belül használható');
  return ctx;
}
